import { useCallback, useEffect, useRef, useState } from 'react';

import { getParentOrigin } from '@/lib/parent-origin';

const LONG_RUNNING_MS = 10_000;
const DISMISSED_KEY = 'response-notifications-dismissed';

const isSupported = typeof window !== 'undefined' && 'Notification' in window;

const readPermission = (): NotificationPermission =>
    isSupported ? Notification.permission : 'denied';

function readDismissed() {
    try {
        return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
        return false;
    }
}

function writeDismissed() {
    try {
        localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
        return;
    }
}

let activeNotification: Notification | undefined;

const isEmbedded = typeof window !== 'undefined' && window.parent !== window;

function notifyParent(title: string, body: string) {
    const origin = getParentOrigin();
    if (!origin) return;
    window.parent.postMessage({ type: 'show_notification', title, body }, origin);
}

function notifyIfAway(title: string, body: string) {
    if (document.hasFocus()) return;
    if (isEmbedded) {
        notifyParent(title, body);
        return;
    }
    if (readPermission() !== 'granted') return;
    activeNotification?.close();
    const notification = new Notification(title, { body });
    notification.onclick = () => {
        window.focus();
        notification.close();
    };
    activeNotification = notification;
}

function useIsLongRunning(isRunning: boolean) {
    const [isLongRunning, setIsLongRunning] = useState(false);

    useEffect(() => {
        if (!isRunning) {
            setIsLongRunning(false);
            return;
        }
        const timer = setTimeout(() => setIsLongRunning(true), LONG_RUNNING_MS);
        return () => clearTimeout(timer);
    }, [isRunning]);

    return isLongRunning;
}

function useNotifyWhenResponseFinishes(
    isTaskRunning: boolean,
    hasPendingQuestion: boolean
) {
    const wasRunning = useRef(false);

    useEffect(() => {
        if (wasRunning.current && !isTaskRunning && !hasPendingQuestion) {
            notifyIfAway('Response ready', 'Your answer is ready.');
        }
        wasRunning.current = isTaskRunning;
    }, [isTaskRunning, hasPendingQuestion]);
}

function useNotifyWhenQuestionAppears(pendingQuestionId?: string) {
    useEffect(() => {
        if (pendingQuestionId) {
            notifyIfAway(
                'Question for you',
                'The assistant needs your input to continue.'
            );
        }
    }, [pendingQuestionId]);
}

interface Options {
    isTaskRunning: boolean;
    pendingQuestionId?: string;
}

export function useResponseNotifications({
    isTaskRunning,
    pendingQuestionId
}: Options) {
    const [permission, setPermission] = useState(readPermission);
    const [dismissed, setDismissed] = useState(readDismissed);
    const isLongRunning = useIsLongRunning(isTaskRunning);

    useNotifyWhenResponseFinishes(isTaskRunning, !!pendingQuestionId);
    useNotifyWhenQuestionAppears(pendingQuestionId);

    // Browsers only grant permission from a user gesture, so call this from a click.
    const enable = useCallback(async () => {
        setPermission(await Notification.requestPermission());
    }, []);

    const dismiss = useCallback(() => {
        setDismissed(true);
        writeDismissed();
    }, []);

    const showPrompt =
        !isEmbedded &&
        isSupported && permission === 'default' && !dismissed && isLongRunning;

    return { showPrompt, enable, dismiss };
}
